import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, MessageSquare } from "lucide-react";

const ThreadList = ({ threads, activeId, me }) => (
  <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="thread-list">
    {threads.length === 0 && <p className="p-5 text-sm text-slate-400" data-testid="no-threads">No conversations yet.</p>}
    {threads.map((t) => (
      <Link key={t.id} to={`/messages/${t.id}`} data-testid={`thread-item-${t.id}`}
        className={`block p-4 transition-colors hover:bg-slate-50 ${t.id === activeId ? "bg-amber-50" : ""}`}>
        <p className="font-semibold text-slate-900 text-sm">{t.starter_id === me ? t.business_name : t.starter_name}</p>
        <p className="text-xs text-slate-500 truncate mt-0.5">{t.last_message || "No messages yet"}</p>
      </Link>
    ))}
  </div>
);

const Conversation = ({ id, me, onSent }) => {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const load = useCallback(() => api.get(`/threads/${id}/messages`).then((r) => setData(r.data)).catch(() => setData(false)), [id]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!text.trim()) return;
    try { await api.post(`/threads/${id}/messages`, { text }); setText(""); await load(); onSent(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };
  if (data === false) return <p className="text-sm text-slate-400" data-testid="thread-not-found">Conversation not found.</p>;
  if (!data) return <p className="text-sm text-slate-400">Loading…</p>;
  const other = data.thread.starter_id === me ? data.thread.business_name : data.thread.starter_name;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="conversation">
      <h2 className="font-heading text-xl font-bold text-slate-950" data-testid="conversation-title">{other}</h2>
      <div className="mt-4 space-y-3 max-h-[28rem] overflow-y-auto" data-testid="conversation-messages">
        {data.messages.length === 0 && <p className="text-sm text-slate-400">Say hello and describe what you need.</p>}
        {data.messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender_id === me ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.sender_id === me ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-800"}`}>
              <p className="text-[10px] opacity-70 mb-0.5">{m.sender_name} · {m.sender_role}</p>
              {m.text}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <Input data-testid="thread-message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" />
        <Button data-testid="thread-message-send-button" onClick={send} className="bg-slate-950 hover:bg-amber-600 text-white rounded-full px-4"><Send size={16} /></Button>
      </div>
    </div>
  );
};

export default function Messages() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const loadThreads = useCallback(() => api.get("/threads").then((r) => setThreads(r.data)).catch(() => {}), []);
  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    if (!id && threads.length && window.innerWidth >= 768) { navigate(`/messages/${threads[0].id}`, { replace: true }); }
  }, [id, threads, navigate]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8" data-testid="messages-page">
      <h1 className="font-heading text-3xl font-extrabold text-slate-950 flex items-center gap-2"><MessageSquare size={26} /> Messages</h1>
      <div className="mt-6 grid md:grid-cols-[300px_1fr] gap-6">
        <ThreadList threads={threads} activeId={id} me={user?.user_id} />
        {id ? <Conversation id={id} me={user?.user_id} onSent={loadThreads} />
          : <p className="text-sm text-slate-400 hidden md:block">Select a conversation.</p>}
      </div>
    </div>
  );
}
